import {
  Button,
  Card,
  Col,
  Progress,
  Row,
  Select,
  Space,
  Table,
  theme,
  Typography,
  Switch,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from "recharts";
import {
  DollarOutlined,
  FundProjectionScreenOutlined,
  CreditCardOutlined,
  WalletOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import { MesServices } from "../Services/MesServices";
import dayjs from "dayjs";
import ReactSpeedometer from "react-d3-speedometer";

const { Text } = Typography;
const Dashboard = () => {
  const [tableData, setTableData] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    solde: 0,
    freels: 0,
    credit: 0,
    debit: 0,
    charge: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [adherent, setAdherent] = useState(null);
  const [documentStats, setDocumentStats] = useState({
    BPI: 0,
    LGI: 0,
    BCI: 0,
    LHI: 0,
  });
  const [exercises, setExercises] = useState([]);
  const mesServices = new MesServices();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [showLine1, setShowLine1] = useState(true);
  const [showLine2, setShowLine2] = useState(true);
  const [darkChart, setDarkChart] = useState(false);
  const numcont = localStorage.getItem("numcont");

  // const data = [
  //   { month: "Jan", n1: 60, n: 40 },
  //   { month: "Feb", n1: 70, n: 50 },
  //   { month: "Mar", n1: 80, n: 30 },
  //   { month: "Apr", n1: 60, n: 20 },
  //   { month: "May", n1: 40, n: 50 },
  //   { month: "Jun", n1: 20, n: 80 },
  //   { month: "Jul", n1: 10, n: 90 },
  //   { month: "Aug", n1: 50, n: 40 },
  //   { month: "Sep", n1: 40, n: 30 },
  //   { month: "Oct", n1: 30, n: 20 },
  //   { month: "Nov", n1: 20, n: 10 },
  //   { month: "Dec", n1: 10, n: 50 },
  // ];

  const isFetched = useRef({
    soldePart: false,
    docsPart: false,
    adherentPart: false,
  });



  useEffect(() => {
    const fetchAdherent = async () => {
      if (!isFetched.current.adherentPart) {
        try {
          const data = await mesServices.getAdherent(numcont.split("-")[0]);
          setAdherent(data);
          isFetched.current.adherentPart = true;
          console.log(data);

        } catch (error) {
          console.error("Erreur lors de la récupération de l'adhérent :", error);
        }
      }
    };

    fetchAdherent();
  }, [numcont]);

  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!isFetched.current.soldePart) {
        try {
          const data = await mesServices.getSoldePart(numcont);
          setSummaryData({
            solde: data.solde || 0,
            freels: data.freels || 0,
            credit: data.credit || 0,
            debit: data.debit || 0,
            charge: data.charge || 0,
          });
          isFetched.current.soldePart = true; // Marquer comme chargé
        } catch (error) {
          console.error("Erreur lors de la récupération des données :", error);
        }
      }
    };

    fetchSummaryData();
  }, [numcont]);

  useEffect(() => {
    const fetchExercices = async () => {
      try {
        const data = await mesServices.getDocExercice(numcont);
        setExercises(data || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des exercices :", error);
      }
    };
    fetchExercices();
  }, [numcont]);

  const ALL_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const fillMissingMonths = (rawData) => {
    const byMonth = {};
    (rawData || []).forEach((item) => {
      byMonth[item.month] = item;
    });
    return ALL_MONTHS.map((m) => byMonth[m] ?? { month: m, n1: 0, n: 0 });
  };

  useEffect(() => {
    const fetchMonthlyConso = async () => {
      try {
        const rawData = await mesServices.getMonthConsoByPart(numcont);
        const filledData = fillMissingMonths(rawData);
        setData(filledData);
        console.log(filledData);
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      }
    };
    fetchMonthlyConso();
  }, [numcont]);


  useEffect(() => {
    const fetchTableData = async () => {
      try {
        setTableLoading(true);
        const data = await mesServices.getDocsPart(numcont, currentYear);
        setTableData(data || []);

        // Calculer les totaux par type de document
        const stats = data.reduce(
          (acc, doc) => {
            acc[doc.typedoc] = (acc[doc.typedoc] || 0) + 1;
            return acc;
          },
          { BPI: 0, LGI: 0, BCI: 0, LHI: 0 }
        );

        setDocumentStats(stats);
      } catch (error) {
        console.error("Erreur lors de la récupération des documents :", error);
      } finally {
        setTableLoading(false);
      }
    };

    fetchTableData();
  }, [numcont, currentYear]);

  const totalDocuments = Object.values(documentStats).reduce(
    (sum, value) => sum + value,
    0
  );

  const columns = [
    {
      title: "Date",
      dataIndex: "datedoc",
      key: "datedoc",
      align: "center",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "—"), // Formatage des dates
      sorter: (a, b) => a.datedoc.localeCompare(b.datedoc),
    },
    {
      title: "Num Doc",
      dataIndex: "numdoc",
      key: "numdoc",
      filters: [
        ...new Set(
          tableData.map((item) => item?.numdoc).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.numdoc === value,

      sorter: (a, b) => a.numdoc.localeCompare(b.numdoc),
    },
    {
      title: "Bénéficiaire",
      dataIndex: "beneficiaire",
      key: "beneficiaire",
      ellipsis: true,
      filters: [
        ...new Set(
          tableData.map((item) => item?.beneficiaire).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.beneficiaire === value,
      sorter: (a, b) => a.beneficiaire.localeCompare(b.beneficiaire),
    },
    {
      title: "Prestataire",
      dataIndex: "fournisseur",
      key: "fournisseur",
      ellipsis: true,
      filters: [
        ...new Set(
          tableData.map((item) => item?.fournisseur).filter((value) => value) // Éliminer les valeurs `null` ou `undefined`
        ),
      ].map((value) => ({
        text: value,
        value: value,
      })),
      onFilter: (value, record) => record.fournisseur === value,
      sorter: (a, b) => a.fournisseur.localeCompare(b.fournisseur),
    },
  ];

  const recentTableData = React.useMemo(() => {
    if (!tableData || tableData.length === 0) return [];
    const getTime = (d) => (d ? dayjs(d).valueOf() : 0);
    const sortedDesc = [...tableData].sort((a, b) => getTime(b.datedoc) - getTime(a.datedoc));
    return sortedDesc.slice(0, 5);
  }, [tableData]);

  const computePercent = (numerator, denominator) => {
    const num = Number(numerator) || 0;
    const den = Number(denominator) || 0;
    if (den <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, num / den));
    return Math.round(ratio * 100);
  };

  const consommationPct = computePercent(summaryData.freels, adherent?.seuil_conso);
  // const consommationPct = 38;
  const endettementPct = computePercent(summaryData.solde, adherent?.seuil_endet);
  const previousYear = currentYear - 1;

  return (
    <div
      style={{
        padding: "20px",
        background: "linear-gradient(115deg, #eef2fc 0%, #e4f5ec 38%, #f9eedd 100%)",
        minHeight: "100vh",
        transition: "background 0.7s ease-in-out"
      }}>

      <h2 style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: 10 }}>
        <FundProjectionScreenOutlined style={{ color: "#1f4ea1", fontSize: 28 }} />
        Tableau de bord
      </h2>
      <Row gutter={16} style={{ marginBottom: "12px" }}>

        <Col flex="1 0 20%">
          <Card
            title="Frais Réels Engagés"
            size="small"
            className="raised-card"
            hoverable
            bordered={false}
            extra={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #52c41a, #73d13d)",
                  boxShadow: "0 4px 12px rgba(82,196,26,0.35)",
                }}
              >
                <FundProjectionScreenOutlined style={{ fontSize: 20, color: "#fff" }} />
              </span>
            }
            style={{
              textAlign: "left",
              background: "linear-gradient(135deg, #f6ffed, #d9f7be)",
              borderRadius: borderRadiusLG,
            }}
          >
            <Text style={{ fontSize: 24 }}>
              <strong>{summaryData.freels.toLocaleString('fr-FR')} CFA</strong>
            </Text>
          </Card>
        </Col>
        <Col flex="1 0 20%">
          <Card
            title="Charges IPM"
            size="small"
            className="raised-card"
            hoverable
            bordered={false}
            extra={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #ff4d4f, #ff7875)",
                  boxShadow: "0 4px 12px rgba(255,77,79,0.35)",
                }}
              >
                <PieChartOutlined style={{ fontSize: 20, color: "#fff" }} />
              </span>
            }
            style={{
              textAlign: "left",
              background: "linear-gradient(135deg, #fff1f0, #ffa39e)",
              borderRadius: borderRadiusLG,
            }}
          >
            <Text style={{ fontSize: 24 }}>
              <strong>{(summaryData.charge || 0).toLocaleString('fr-FR')} CFA</strong>
            </Text>
          </Card>
        </Col>
        <Col flex="1 0 20%">
          <Card
            title="Montant Total Dû"
            size="small"
            className="raised-card"
            hoverable
            bordered={false}
            extra={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #faad14, #ffc53d)",
                  boxShadow: "0 4px 12px rgba(250,173,20,0.35)",
                }}
              >
                <CreditCardOutlined style={{ fontSize: 20, color: "#fff" }} />
              </span>
            }
            style={{
              textAlign: "left",
              background: "linear-gradient(135deg, #fff7e6, #ffd591)",
              borderRadius: borderRadiusLG,
            }}
          >
            <Text style={{ fontSize: 24 }}>
              <strong>{summaryData.debit.toLocaleString('fr-FR')} CFA</strong>
            </Text>
          </Card>
        </Col>
        <Col flex="1 0 20%">
          <Card
            title="Total Remboursé"
            size="small"
            className="raised-card"
            hoverable
            bordered={false}
            extra={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #722ed1, #9254de)",
                  boxShadow: "0 4px 12px rgba(114,46,209,0.35)",
                }}
              >
                <WalletOutlined style={{ fontSize: 20, color: "#fff" }} />
              </span>
            }
            style={{
              textAlign: "left",
              background: "linear-gradient(135deg, #f9f0ff, #d3adf7)",
              borderRadius: borderRadiusLG,
            }}
          >
            <Text style={{ fontSize: 24 }}>
              <strong>{summaryData.credit.toLocaleString('fr-FR')} CFA</strong>
            </Text>
          </Card>
        </Col>
        <Col flex="1 0 20%">
          <Card
            title="Solde Actuel"
            size="small"
            className="raised-card"
            hoverable
            bordered={false}
            extra={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #1f4ea1, #3c94c8)",
                  boxShadow: "0 4px 12px rgba(31,78,161,0.35)",
                }}
              >
                <DollarOutlined style={{ fontSize: 20, color: "#fff" }} />
              </span>
            }
            style={{
              textAlign: "left",
              background: "linear-gradient(135deg, #e6f7ff, #bae7ff)",
              borderRadius: borderRadiusLG,
            }}
          >
            <Text style={{ fontSize: 24 }}>
              <strong>{summaryData.solde.toLocaleString('fr-FR')} CFA</strong>
            </Text>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginBottom: "12px" }}>
        {/* Graphique des consommations mensuelles */}
        <Col span={12}>
          <Card
            title="Consommations Mensuelles"
            size="small"
            hoverable
            bordered={false}
            extra={
              <Switch
                checked={darkChart}
                onChange={setDarkChart}
                size="small"
                checkedChildren="Sombre"
                unCheckedChildren="Clair"
              />
            }
            style={{ textAlign: "left", borderRadius: borderRadiusLG, backgroundColor: colorBgContainer }}
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart
                width={700}
                height={300}
                data={data}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="fillN1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={darkChart ? "#1f4ea1" : "#001529"} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={darkChart ? "#1f4ea1" : "#001529"} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="fillN" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={darkChart ? "#36cfc9" : "#00c49f"} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={darkChart ? "#36cfc9" : "#00c49f"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={darkChart ? "#303030" : "#f0f0f0"} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: darkChart ? "#bfbfbf" : "#595959" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: darkChart ? "#bfbfbf" : "#595959" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: darkChart ? "#141414" : "#ffffff",
                    borderColor: darkChart ? "#303030" : "#f0f0f0",
                    color: darkChart ? "#bfbfbf" : "#595959",
                  }}
                />
                {/*  <Legend /> */}
                {showLine1 && (
                  <Area
                    type="monotone"
                    dataKey="n1"
                    fill="url(#fillN1)"
                    stroke="none"
                    name={`${previousYear}`}
                    tooltipType="none"
                    legendType="none"
                  />
                )}
                {showLine2 && (
                  <Area
                    type="monotone"
                    dataKey="n"
                    fill="url(#fillN)"
                    stroke="none"
                    name={`${currentYear}`}
                    tooltipType="none"
                    legendType="none"
                  />
                )}
                {showLine1 && (
                  <Line
                    type="monotone"
                    dataKey="n1"
                    stroke={darkChart ? "#1f4ea1" : "#001529"}
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, stroke: darkChart ? "#1f4ea1" : "#001529", fill: "#fff" }}
                    activeDot={{ r: 6 }}
                    name={`${previousYear}`}
                  />
                )}
                {showLine2 && (
                  <Line
                    type="monotone"
                    dataKey="n"
                    stroke={darkChart ? "#36cfc9" : "#00c49f"}
                    strokeWidth={3}
                    dot={{ r: 4, strokeWidth: 2, stroke: darkChart ? "#36cfc9" : "#00c49f", fill: "#fff" }}
                    activeDot={{ r: 6 }}
                    name={`${currentYear}`}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            {/* Boutons séparés */}
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <Space size="large">
                <Button
                  onClick={() => setShowLine1(!showLine1)}
                  style={{
                    color: showLine1 ? "#001529" : "grey",
                    textDecoration: showLine1 ? "none" : "line-through",
                  }}
                >
                  {previousYear}
                </Button>
                <Button
                  onClick={() => setShowLine2(!showLine2)}
                  style={{
                    color: showLine2 ? "#00c49f" : "grey",
                    textDecoration: showLine2 ? "none" : "line-through",
                  }}
                >
                  {currentYear}
                </Button>
              </Space>
            </div>
          </Card>
        </Col>
        {/* Taux de Création de Documents */}
        <Col span={12}>
          <Card
            title={
              <span style={{ textAlign: "left", display: "block" }}>
                Documents Créés (Total : {totalDocuments})
              </span>
            }
            size="small"
            hoverable
            bordered={false}
            extra={
              <Select
                value={currentYear}
                onChange={(val) => setCurrentYear(val)}
                size="small"
                style={{ width: 90 }}
                options={exercises.map((ex) => ({ label: ex, value: ex }))}
              />
            }
            style={{ marginBottom: 12, borderRadius: borderRadiusLG, backgroundColor: colorBgContainer }}
          >
            {/* Bons de Pharmacie */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Text>Bons de Pharmacie ({documentStats.BPI})</Text>
              <Progress
                percent={
                  totalDocuments > 0
                    ? Math.round((documentStats.BPI / totalDocuments) * 100)
                    : 0
                }
                status="active"
                strokeWidth={12}
                size="default"
                strokeColor={{ '0%': '#ff7a45', '100%': '#ffa940' }}
                trailColor="#f0f0f0"
                format={(p) => `${p}%`}
                style={{ width: "70%" }}
              />
            </div>

            {/* Lettres de Garantie */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Text>Lettres de Garantie ({documentStats.LGI})</Text>
              <Progress
                percent={
                  totalDocuments > 0
                    ? Math.round((documentStats.LGI / totalDocuments) * 100)
                    : 0
                }
                status="active"
                strokeWidth={12}
                size="default"
                strokeColor={{ '0%': '#13c2c2', '100%': '#3c94c8' }}
                trailColor="#f0f0f0"
                format={(p) => `${p}%`}
                style={{ width: "70%" }}
              />
            </div>

            {/* Bons Optique */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <Text>Bons Optique ({documentStats.BCI})</Text>
              <Progress
                percent={
                  totalDocuments > 0
                    ? Math.round((documentStats.BCI / totalDocuments) * 100)
                    : 0
                }
                status="active"
                strokeWidth={12}
                size="default"
                strokeColor={{ '0%': '#722ed1', '100%': '#9254de' }}
                trailColor="#f0f0f0"
                format={(p) => `${p}%`}
                style={{ width: "70%" }}
              />
            </div>

            {/* Lettres d'Hospitalisation */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Lettres d'Hospitalisation ({documentStats.LHI})</Text>
              <Progress
                percent={
                  totalDocuments > 0
                    ? Math.round((documentStats.LHI / totalDocuments) * 100)
                    : 0
                }
                status="active"
                strokeWidth={12}
                size="default"
                strokeColor={{ '0%': '#eb2f96', '100%': '#f5222d' }}
                trailColor="#f0f0f0"
                format={(p) => `${p}%`}
                style={{ width: "70%" }}
              />
            </div>
          </Card>


          {/* Notification*/}
          <Card
            title={
              <span style={{ textAlign: "left", display: "block" }}>

                DEPASSEMENTS
              </span>
            }
            size="small"
            hoverable
            bordered={false}
            style={{ borderRadius: borderRadiusLG, backgroundColor: colorBgContainer }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div style={{ textAlign: "center" }}>
                <ReactSpeedometer
                  value={consommationPct}
                  minValue={0}
                  maxValue={100}
                  customSegmentStops={[0, 50, 75, 100]}
                  segmentColors={["#52c41a", "#fa8c16", "#f5222d"]}
                  needleColor="#595959"
                  textColor="#595959"
                  width={220}
                  height={140}
                  currentValueText={`${consommationPct}%`}
                />
                <div>
                  <Text strong>Consommation</Text>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <ReactSpeedometer
                  value={endettementPct}
                  minValue={0}
                  maxValue={100}
                  customSegmentStops={[0, 50, 75, 100]}
                  segmentColors={["#52c41a", "#fa8c16", "#f5222d"]}
                  needleColor="#595959"
                  textColor="#595959"
                  width={220}
                  height={140}
                  currentValueText={`${endettementPct}%`}
                />
                <div>
                  <Text strong>Endettement</Text>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Card
            title={
              <span style={{ textAlign: "left", display: "block" }}>
                Documents Récents
              </span>
            }
            size="small"
            hoverable
            bordered={false}
            style={{ borderRadius: borderRadiusLG, backgroundColor: colorBgContainer }}
          >
            <Table
              dataSource={recentTableData}
              columns={columns}
              pagination={false}
              loading={tableLoading}
              tableLayout="fixed"
              scroll={{ x: true }}
              rowKey="numdoc" // Assurez-vous que chaque ligne a une clé unique
              bordered={false}
              size="small"
              style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
              className="custom-header-table my-custom-table"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
